import React, { useState, useEffect, useMemo } from "react";
import Modal from "components/modal";
import Button from "components/button";
import { useDispatch, useSelector } from "react-redux";
import {
  getOrderItemsList,
  resetOrderItemsNoData,
  assignItem,
} from "store/containerSlice";
import InputField from "components/fields/InputField";
import { RiErrorWarningFill } from "react-icons/ri";
import { showToast } from "components/toast";
import LoaderButton from "components/button/LoaderButton";
import { getUsersList } from "store/orderSlice";
import SearchSelectField from "components/fields/SearchSelectField";

function AssignOrderModal({ isOpen, onClose, item, refreshList }) {
  const dispatch = useDispatch();

  const closeModal = () => {
    dispatch(resetOrderItemsNoData());
    onClose();
  };
  const {
    orderItemsListLoading,
    orderItemsListData,
    orderItemsListError,
    assignLoading,
  } = useSelector((state) => state.containers);
  const { userListData } = useSelector((state) => state.orders);

  const [userSearch, setUserSearch] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [orderIdSearch, setOrderIdSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedForAssign, setSelectedForAssign] = useState(null);
  const [quantity, setQuantity] = useState(item?.totalQty);

  useEffect(() => {
    dispatch(getUsersList({ page: 1, limit: 10, search: searchText }));
  }, [dispatch, searchText]);

  const orderForOptions = useMemo(() => {
    if (!Array.isArray(userListData)) return [];
    return userListData.map((o) => ({
      value: o._id,
      label: `${o.firstname} ${o.lastname}`,
    }));
  }, [userListData]);

  useEffect(() => {
    dispatch(
      getOrderItemsList({
        orderNo: orderIdSearch || null,
        userName: userSearch?.label || null,
        totalQty: Number(quantity) > 0 ? Number(quantity) : null,
      })
    );
  }, [dispatch, userSearch, orderIdSearch, quantity]);

  const handelAssign = async (selectedItem) => {
    if (!selectedItem) {
      console.error("Selected item is not available!");
      showToast?.error("Please select an item before assigning!");
      return;
    }
    if (!item?._id) {
      console.error("Container item ID is not available!");
      showToast?.error("Container item ID is not available!");
      return;
    }

    try {
      const response = await dispatch(
        assignItem({
          containerItemId: item._id,
          orderItemId: selectedItem._id,
        })
      );

      if (
        response?.payload?.statuscode === 201 ||
        response?.payload?.statuscode === 200
      ) {
        setConfirmOpen(false);
        setSelectedForAssign(null);
        closeModal?.();
        refreshList?.();
      } else {
        console.error(
          "Assign failed:",
          response?.payload?.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Assign failed:", error);
    }
  };

  const openConfirm = (it) => {
    setSelectedForAssign(it);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (assignLoading) return;
    setConfirmOpen(false);
    setSelectedForAssign(null);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        className="min-h-[400px] max-w-3xl"
      >
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Assign Items (Total Quantity : {item?.totalQty || 0})
        </h2>

        <div className="grid grid-cols-1 gap-2 p-4 md:grid-cols-3">
          <SearchSelectField
            label="Order For*"
            options={orderForOptions}
            placeholder="Search users..."
            value={userSearch}
            onChange={(selected) => {
              setUserSearch(selected);
              if (!selected) {
                setSearchText("");
              }
            }}
            onInputChange={(val) => setSearchText(val)}
            isClearable
          />

          <InputField
            id="orderIdSearch"
            variant="auth"
            label="Search by Order No"
            placeholder="Enter order no"
            type="text"
            value={orderIdSearch}
            onChange={(e) => setOrderIdSearch(e.target.value)}
          />

          <InputField
            id="quantity"
            variant="auth"
            label="Search by quantity"
            placeholder="Enter quantity"
            type="number"
            value={quantity}
            onChange={(e) => {
              let value = e.target.value;

              setQuantity(value);
            }}
          />
        </div>

        {/* Items list */}
        <div className="custom-scrollbar max-h-80 overflow-y-auto px-4 pb-4">
          {orderItemsListError ? (
            <div className="flex items-center justify-center gap-2 text-red-500">
              <RiErrorWarningFill className="h-6 w-6" />
              <span>{orderItemsListError}</span>
            </div>
          ) : orderItemsListData?.length === 0 && orderItemsListLoading ? (
            <ul className="animate-pulse divide-y divide-gray-200 dark:divide-gray-700">
              {[...Array(2)].map((_, i) => (
                <li key={i} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-gray-300 dark:bg-gray-700"></div>
                    <div className="space-y-2">
                      <div className="h-4 w-32 rounded bg-gray-300 dark:bg-gray-700"></div>
                      <div className="h-3 w-24 rounded bg-gray-300 dark:bg-gray-700"></div>
                    </div>
                  </div>
                  <div className="h-6 w-16 rounded bg-gray-300 dark:bg-gray-700"></div>
                </li>
              ))}
            </ul>
          ) : orderItemsListData?.length > 0 ? (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {orderItemsListData.map((i) => (
                <li
                  key={i?._id}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex items-center gap-3">
                    <img
                      src={i.picture}
                      alt={i.name || i.description || "Item"}
                      className="h-10 w-10 rounded-md border object-cover"
                    />
                    <div>
                      <p className="font-medium text-navy-700 dark:text-white">
                        {i.name || i.description || "Unnamed item"}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {i.description} • Qty: {i.totalQty}
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        User:{" "}
                        {i.orderForUser
                          ? `${i.orderForUser.firstname} ${i.orderForUser.lastname}`
                          : "Unknown"}{" "}
                        • Order No: {i.orderInfo?.orderNo || "N/A"}
                      </p>
                    </div>
                  </div>
                  {i.totalQty === item?.totalQty && (
                    <Button
                      onClick={() => openConfirm(i)}
                      className="py-1 text-sm"
                    >
                      Assign
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className=" text-center  text-gray-500 dark:text-gray-400">
              No matching items.
            </p>
          )}
        </div>
      </Modal>

      <Modal isOpen={confirmOpen} onClose={closeConfirm} className="max-w-lg">
        <h3 className="border-b py-3 pl-2 text-lg font-semibold text-navy-700 dark:text-white">
          Confirm Assignment
        </h3>

        <div className="p-4">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Are you sure you want to assign this item to the selected order?
          </p>

          {/* quick summary */}
          {selectedForAssign && (
            <div className="mt-3 rounded-lg border p-3 text-sm dark:border-navy-600">
              <div className="flex items-center gap-3">
                <img
                  src={selectedForAssign.picture}
                  alt={
                    selectedForAssign.name ||
                    selectedForAssign.description ||
                    "Item"
                  }
                  className="h-12 w-12 rounded-md border object-cover"
                />
                <div>
                  <div className="font-medium">
                    {selectedForAssign.name || selectedForAssign.description}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400">
                    Qty: {selectedForAssign.totalQty} • Order No:{" "}
                    {selectedForAssign.orderInfo?.orderNo || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={closeConfirm}
              disabled={assignLoading}
            >
              Cancel
            </Button>
            <LoaderButton
              isLoading={assignLoading}
              onClick={() => handelAssign(selectedForAssign)}
            >
              Yes, assign
            </LoaderButton>
          </div>
        </div>
      </Modal>
    </>
  );
}

export default AssignOrderModal;
